/**
 * voicePolicy — pure decisions for the Voice Manager (L2): dedup/throttle + voice stealing.
 *
 * Separated from the node plumbing so the *policy* (which voice dies when the pool is full,
 * whether a cue is allowed to fire) is unit-testable without an AudioContext. This is the
 * scale backbone — the bounded resource everything competes for. See architecture §3.
 */

import type { Cue } from './audioTypes';

/** Minimal voice metadata the policy reasons over (no Web Audio nodes). */
export interface VoiceInfo {
  id: number;
  cueId: string;
  priority: number;   // 0..1
  startedAt: number;  // ms (audio clock)
  /** The logical fire() this grain belongs to — maxStack counts PLAYS
   *  (distinct fireIds), not grains: a 2-grain sequence used to saturate
   *  its own maxStack=2 on its first fire (2026-07-11 root find, the 🔊
   *  XY test battery's 50% silent-drop rate). */
  fireId?: number;
  /** Scheduled end (audio-clock ms, + pad). Voices past this count as dead
   *  even if onended never delivered (grains scheduled into a suspended
   *  context / throttled tabs) — stale bookkeeping must not mute a cue. */
  expectedEndAtMs?: number;
}

/**
 * Decide whether a cue may fire now.
 *  - dedup: same cue fired within `minIntervalMs` is collapsed.
 *  - maxStack: cap concurrent PLAYS of this cue (see VoiceInfo.fireId).
 * Returns true if the cue should play.
 */
export function shouldPlay(
  cue: Cue,
  now: number,
  lastFiredAt: number | undefined,
  activeCountOfCue: number,
): boolean {
  if (cue.minIntervalMs != null && lastFiredAt != null && now - lastFiredAt < cue.minIntervalMs) {
    return false;
  }
  if (cue.maxStack != null && activeCountOfCue >= cue.maxStack) {
    return false;
  }
  return true;
}

/**
 * Choose which active voice to evict to make room for an incoming cue, when the pool is full.
 * Policy: only steal a voice whose priority is <= the incoming priority; among those, evict the
 * lowest priority, tie-broken by oldest (smallest startedAt). Returns the victim's id, or null
 * if nothing is stealable (→ caller drops the incoming sound rather than killing something louder).
 */
export function selectVoiceToSteal(
  active: readonly VoiceInfo[],
  incomingPriority: number,
): number | null {
  let victim: VoiceInfo | null = null;
  for (const v of active) {
    if (v.priority > incomingPriority) continue; // never steal something more important
    if (
      victim === null ||
      v.priority < victim.priority ||
      (v.priority === victim.priority && v.startedAt < victim.startedAt)
    ) {
      victim = v;
    }
  }
  return victim ? victim.id : null;
}
